// URL normalization and validation utilities
// Shared across packages that handle user-entered URLs

export interface SocialUrlValidationResult {
  normalizedUrl: string;
  isValid: boolean;
}

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

function looksLikeBareDomain(value: string): boolean {
  if (!value || /\s/.test(value)) return false;
  if (value.includes('://')) return false;
  if (value.startsWith('//')) return false;
  return value.includes('.');
}

// Prepends https:// to bare domains (e.g. "google.com" → "https://google.com")
export function normalizeSocialUrl(value: string): string {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';

  if (looksLikeBareDomain(trimmedValue)) {
    return `https://${trimmedValue}`;
  }

  return trimmedValue;
}

// Strict check for a user-entered web link (social profile, website, application link):
// http(s) only, and a hostname with at least two non-empty labels. `new URL()` alone accepts
// "https://x" (no TLD) and "javascript:…"; any number of subdomains is allowed (BUG-WEB-10).
export function isValidWebUrl(url: string): boolean {
  if (!url || /\s/.test(url)) return false;
  try {
    const { protocol, hostname } = new URL(url);
    const labels = hostname.split('.');
    return ALLOWED_PROTOCOLS.has(protocol) && labels.length >= 2 && labels.every(Boolean);
  } catch {
    return false;
  }
}

export function validateSocialUrl(value: string): SocialUrlValidationResult {
  const normalizedUrl = normalizeSocialUrl(value);
  if (!normalizedUrl) {
    return { normalizedUrl, isValid: false };
  }

  try {
    const parsedUrl = new URL(normalizedUrl);
    return {
      normalizedUrl,
      isValid: ALLOWED_PROTOCOLS.has(parsedUrl.protocol),
    };
  } catch {
    return { normalizedUrl, isValid: false };
  }
}
